'use client';

import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from '@/lib/i18n/navigation';
import { cn } from '@/lib/utils';
import { TarotHeroAnimation } from '@/components/home/tarot-hero-animation';
import { EditorialHeroAnimation } from '@/components/home/editorial-hero-animation';

export interface HeroSlide {
  id: string;
  label: string; // texto en nav inferior, ej. "NOVEDADES", "CÓDIGO ÁUREO"
  eyebrow: string;
  title: string;
  subtitle?: string;
  cta: { href: string; label: string };
  imageSrc: string;
  imageAlt: string;
  /** Enfoque horizontal de la imagen: 0 left, 50 center, 100 right */
  objectPosition?: string;
  /**
   * Variante del visual del slide.
   * - undefined (default): renderiza <Image> con `imageSrc`.
   * - 'tarot': carta flip 3D sobre eje central.
   * - 'editorial': página de revista girando sobre bisagra izquierda.
   */
  variant?: 'tarot' | 'editorial';
  /**
   * Tamaño del título. Default `hero` (lo más grande). Usar `h1` para
   * títulos largos que no caben en type-hero.
   */
  titleSize?: 'hero' | 'h1';
  /**
   * Overlay oscuro adicional. Usar cuando la imagen original viene sobre
   * fondo blanco/seamless y necesita tonarse para integrar con la paleta
   * noir del resto del carousel.
   */
  darkTint?: boolean;
}

interface HeroCarouselProps {
  slides: HeroSlide[];
  /** Ms entre auto-advance. 0 desactiva. */
  autoplayMs?: number;
}

/**
 * Hero Carousel — single-viewport horizontal slides editoriales.
 *
 * Patrón observado en zarahome.com (docs/patterns-zh-observed.md §3):
 * home = carrusel de slides full-viewport navegables con tabs textuales inferior.
 * Cross-fade entre slides, no slide horizontal físico.
 * Respeta prefers-reduced-motion (desactiva autoplay).
 */
export function HeroCarousel({ slides, autoplayMs = 7000 }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const reduced = useReducedMotion();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (reduced || !autoplayMs) {
      return;
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      setActive((i) => (i + 1) % slides.length);
    }, autoplayMs);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [active, autoplayMs, slides.length, reduced]);

  if (!slides.length) {
    return null;
  }
  const current = slides[active]!;

  return (
    <section
      className="relative h-[100svh] w-full overflow-hidden bg-[var(--color-bg)]"
      aria-roledescription="carrusel"
      aria-label="Editoriales NQORO"
    >
      {/* Capa de visuales con cross-fade */}
      {slides.map((s, i) => (
        <motion.div
          key={s.id}
          className="absolute inset-0"
          initial={false}
          animate={{ opacity: i === active ? 1 : 0 }}
          transition={{ duration: reduced ? 0 : 1.2, ease: [0.22, 1, 0.36, 1] }}
          aria-hidden={i === active ? 'false' : 'true'}
        >
          {s.variant === 'tarot' ? (
            <TarotHeroAnimation />
          ) : s.variant === 'editorial' ? (
            <EditorialHeroAnimation />
          ) : (
            <>
              <Image
                src={s.imageSrc}
                alt={s.imageAlt}
                fill
                priority={i === 0}
                sizes="100vw"
                quality={95}
                /* Cloudinary URLs (res.cloudinary.com) ya optimizan con
                   f_auto,q_auto en la transformación; next/image
                   los re-escalaría a 900px y causaría stretch 1.6x en
                   viewports anchos. Bypass: unoptimized para externos. */
                unoptimized={s.imageSrc.startsWith('https://res.cloudinary.com')}
                className="object-cover"
                style={{ objectPosition: s.objectPosition ?? 'center' }}
              />
              {s.darkTint ? (
                // Gradiente izquierda→derecha: oscurece el lado del overlay
                // tipográfico (izq) y deja el producto visible a la derecha.
                // Úsalo cuando la imagen tiene fondo blanco/seamless.
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[var(--color-bg)]/80 via-[var(--color-bg)]/35 to-transparent" />
              ) : null}
              {/* Gradiente sutil bottom→top para legibilidad de overlay */}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--color-bg)]/75 via-[var(--color-bg)]/10 to-transparent" />
            </>
          )}
        </motion.div>
      ))}

      {/* Overlay tipográfico con crossfade sync */}
      <div className="relative z-10 flex h-full flex-col justify-end px-4 pb-32 md:px-16 md:pb-40 lg:px-24">
        <motion.div
          key={current.id + '-text'}
          initial={reduced ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduced ? 0 : 0.72, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
          className="max-w-3xl"
        >
          <p className="type-eyebrow mb-4 text-[var(--color-fg)]/80">{current.eyebrow}</p>
          <h1
            className={cn(
              'mb-6 text-[var(--color-fg)]',
              current.titleSize === 'h1' ? 'type-h1' : 'type-hero'
            )}
          >
            {current.title}
          </h1>
          {current.subtitle ? (
            <p className="type-editorial-lead mb-8 max-w-xl text-[var(--color-fg)]/85">
              {current.subtitle}
            </p>
          ) : null}
          <Link
            href={current.cta.href}
            className="type-nav inline-flex h-14 items-center justify-center rounded-sm bg-[var(--color-accent)] px-10 text-[var(--color-accent-fg)] transition-colors hover:bg-[var(--color-accent)]"
          >
            {current.cta.label}
          </Link>
        </motion.div>
      </div>

      {/* Tabs textuales inferiores — estilo Zara Home */}
      <div className="absolute inset-x-0 bottom-0 z-[var(--z-dropdown)] border-t border-[var(--color-fg)]/10 bg-[var(--color-bg)]/40 backdrop-blur-sm">
        <div className="no-scrollbar mx-auto flex max-w-[1440px] items-center gap-8 overflow-x-auto px-4 py-5 md:px-16 md:py-6">
          {slides.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActive(i)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') {
                  e.preventDefault();
                  setActive((prev) => (prev + 1) % slides.length);
                } else if (e.key === 'ArrowLeft') {
                  e.preventDefault();
                  setActive((prev) => (prev - 1 + slides.length) % slides.length);
                } else if (e.key === 'Home') {
                  e.preventDefault();
                  setActive(0);
                } else if (e.key === 'End') {
                  e.preventDefault();
                  setActive(slides.length - 1);
                }
              }}
              className={cn(
                'type-nav relative shrink-0 rounded-xs py-1 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[var(--color-accent)]',
                i === active
                  ? 'text-[var(--color-fg)]'
                  : 'text-[var(--color-fg)]/70 hover:text-[var(--color-fg)]'
              )}
              aria-current={i === active ? 'page' : undefined}
              aria-label={`Ver slide ${i + 1} de ${slides.length}: ${s.label}`}
            >
              {s.label}
              {i === active ? (
                <motion.span
                  layoutId="hero-tab-underline"
                  className="absolute right-0 -bottom-1 left-0 h-px bg-[var(--color-accent)]"
                />
              ) : null}
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
